import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/filter';
import 'rxjs/add/observable/empty';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/take';
import 'rxjs/add/operator/mergeMap';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { BACKLOG } from '../../model/constants.tsx';
import { SET_APP_DATA } from '../../state/actions/app/index.tsx';
import {
	CARD_CREATE_GLOBAL,
	type CardCreateGlobalAction,
} from '../../state/actions/cards/create/index.tsx';
import { cardsMoveRequest } from '../../state/actions/cards/move/index.tsx';
import { isCardMoveDisabled } from '../../state/selectors/app/index.tsx';
import {
	getCards,
	getLastSelectedCard,
	makeGetCardListIdByCardId,
} from '../../state/selectors/cards/index.tsx';
import type { Action, ActionsObservable, Dependencies, MiddlewareAPI } from '../../state/types.tsx';

export const cardRankAfterGlobalIssueCreateEpic = (
	action$: ActionsObservable,
	store: MiddlewareAPI,
	dependencies?: Dependencies,
): Observable<Action> =>
	action$.ofType(CARD_CREATE_GLOBAL).switchMap((cardCreateGlobalAction: CardCreateGlobalAction) => {
		const state = store.getState();
		const lastSelectedCardId = getLastSelectedCard(state);
		const cardMoveDisabled = isCardMoveDisabled(state);

		const listId = lastSelectedCardId && makeGetCardListIdByCardId(state)(lastSelectedCardId);

		// We should not rank created issues from GIC when:
		// - there is no selected card
		// - the board is not ordered by rank (this will also filter out TMP projects)
		// - the card is not part of any card list
		// - the card is not on `BACKLOG` card list
		if (!lastSelectedCardId || cardMoveDisabled || !listId || listId !== BACKLOG) {
			return Observable.empty();
		}

		const {
			payload: { globalIssues },
		} = cardCreateGlobalAction;

		const analyticsEvent =
			dependencies && dependencies.createAnalyticsEvent
				? dependencies.createAnalyticsEvent({})
				: // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					({} as UIAnalyticsEvent);

		return action$
			.ofType(SET_APP_DATA)
			.filter(() => {
				const cards = getCards(store.getState());
				return globalIssues.every(({ issueId }) => cards[issueId]);
			})
			.take(1)
			.flatMap(() => {
				const cardIds = globalIssues.map((createdIssue) => parseInt(createdIssue.issueId, 10));
				return Observable.of(
					cardsMoveRequest(
						{
							cardIds,
							cardListId: listId,
							isBetweenLists: false,
							beforeCardId: null,
							afterCardId: lastSelectedCardId,
						},
						analyticsEvent,
					),
				);
			});
	});
